import { type FC, useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { PAGE_TITLES, PATHS } from 'routes/routes.constants';
import * as z from 'zod';

import useRequiredParams from '@polyai/common/hooks/useRequiredParams';
import { useRouter } from '@polyai/common/hooks/useRouter';
import { Loader } from '@polyai/ui/components/atoms/Loader';
import DefaultScreenHeader from '@polyai/ui/components/molecules/ScreenHeader';

import type {
  BaseApiConfig,
  MetricConfig,
  Origin,
} from 'api/resources/apiConfig/types';
import EmptyState, { EmptyStatus } from 'components/molecules/EmptyState';
import FormProvider from 'components/molecules/Form';
import { JupiterCardList } from 'components/molecules/JupiterCard';
import SaveChangesDrawer from 'components/molecules/SaveChangesDrawer';
import useApiConfig from 'hooks/api/apiConfig/useApiConfig';
import useApiConfigMetrics from 'hooks/api/apiConfig/useApiConfigMetrics';
import useApiConfigPatch from 'hooks/api/apiConfig/useApiConfigPatch';
import useProjects from 'hooks/useProjects';
import { generateNextPath } from 'lib/routeHelper';
import withPermissions from 'screens/withPermissions';
import ApiConfigCard from './components/ApiConfigCard';
import DATA_ACCESS_API_CONFIGURATION from './DataAccessApiConfiguration.uiCopy';
import {
  areAllMetricsSelected,
  buildCompositeMetricOptions,
  filterDuplicateCustomMetrics,
  parseCompositeValues,
} from './helpers';

const Option = z.object({
  value: z.string(),
  label: z.string(),
});
const ApiConfigPerProject = z.object({
  changed_metrics: z.array(Option).optional(),
  show_transcripts: z.boolean().optional(),
});

export const ApiConfigFormSchema = z.object({
  configs: z.record(ApiConfigPerProject),
});

export type ApiConfigFormInput = z.input<typeof ApiConfigFormSchema>;
export type ApiConfigFormOutput = z.output<typeof ApiConfigFormSchema>;

const ApiConfiguration: FC = () => {
  const { projects, isLoadingProjects } = useProjects();
  const { data: configsData, isLoading: isLoadingApiConfigs } = useApiConfig();
  const { mutateAsync: patchApiConfig, isLoading: isLoadingPatchApiConfig } =
    useApiConfigPatch();
  const { data: metrics, isLoading: isLoadingMetrics } = useApiConfigMetrics();
  const { accountId } = useRequiredParams<{ accountId: string }>();
  const { pushUrl } = useRouter();

  const currentApiConfigs = useMemo(() => {
    // Build default values shaped like ApiConfigFormSchema
    const map: Record<string, z.infer<typeof ApiConfigPerProject>> = {};

    for (const project of projects ?? []) {
      // find matching config for this project_id
      const c = configsData?.configs.find(cfg => cfg.project_id === project.id);

      // Transform metrics array to composite values (name__origin)
      const customMetrics = metrics?.custom_metrics?.[project.id] ?? [];
      const standardMetrics = metrics?.standard_metrics ?? [];
      const selectedNames = new Set(c?.metrics?.filter(m => m !== '*') ?? []);

      // Filter out custom metrics that have the same name as standard metrics
      const filteredCustomMetrics = filterDuplicateCustomMetrics(
        customMetrics,
        standardMetrics,
      );

      const transformedMetrics = [
        ...buildCompositeMetricOptions(filteredCustomMetrics, 'custom'),
        ...buildCompositeMetricOptions(standardMetrics, 'standard'),
      ].filter(opt => selectedNames.has(opt.label));

      // Check if all metrics are selected
      const allMetricsSelected = areAllMetricsSelected(
        selectedNames,
        filteredCustomMetrics,
        standardMetrics,
      );

      // If all metrics are selected, include wildcard + all options (like "All agents" does)
      const finalMetrics = allMetricsSelected
        ? [{ value: '*', label: 'All metrics' }, ...transformedMetrics]
        : transformedMetrics;

      map[project.id] = {
        changed_metrics: finalMetrics,
        show_transcripts: c?.show_transcripts ?? false,
      };
    }

    return { configs: map };
  }, [projects, configsData, metrics]);

  const methods = useForm<ApiConfigFormInput, unknown, ApiConfigFormOutput>({
    resolver: zodResolver(ApiConfigFormSchema),
    defaultValues: currentApiConfigs,
    mode: 'onSubmit',
    shouldFocusError: false,
  });

  const { reset, handleSubmit, formState } = methods;
  const { isDirty } = formState;

  // Reset form when data loads
  useEffect(() => {
    if (!isLoadingApiConfigs && configsData) {
      reset(currentApiConfigs);
    }
  }, [isLoadingApiConfigs, configsData, currentApiConfigs, reset]);

  const handleDiscard = () => {
    reset(currentApiConfigs);
  };

  const onSubmit: SubmitHandler<ApiConfigFormOutput> = async formData => {
    const configs: BaseApiConfig[] = [];

    // Loop through each project in the form
    Object.keys(formData.configs).forEach(projectId => {
      const formConfig = formData.configs[projectId];

      // Get all available metrics for this project (standard + custom)
      const customMetrics = metrics?.custom_metrics?.[projectId] ?? [];
      const standardMetrics = metrics?.standard_metrics ?? [];

      // Filter out custom metrics that have the same name as standard metrics
      const filteredCustomMetrics = filterDuplicateCustomMetrics(
        customMetrics,
        standardMetrics,
      );

      const allAvailableMetrics = [
        ...filteredCustomMetrics.map(name => ({
          name,
          origin: 'custom' as Origin,
        })),
        ...standardMetrics.map(name => ({
          name,
          origin: 'standard' as Origin,
        })),
      ];

      // Parse composite values from form
      const selectedCompositeValues = parseCompositeValues(
        formConfig.changed_metrics,
      );

      const completeMetrics: MetricConfig[] = allAvailableMetrics.map(
        ({ name, origin }) => ({
          name,
          api: selectedCompositeValues.has(`${name}__${origin}`),
          origin,
        }),
      );

      configs.push({
        project_id: projectId,
        show_transcripts: formConfig.show_transcripts ?? false,
        changed_metrics: completeMetrics,
      });
    });

    // Don't submit if there are no configs
    if (configs.length === 0) {
      return;
    }

    // Send to backend, then redirect on success
    try {
      await patchApiConfig(configs, {
        onSuccess: () => {
          pushUrl(generateNextPath(PATHS.DATA_ACCESS, { accountId }));
        },
      });
    } catch (error) {
      // Error is already handled by the hook (shows error toast)
      console.error('Failed to save API config:', error);
    }
  };

  const isLoading =
    isLoadingProjects || isLoadingApiConfigs || isLoadingMetrics;

  if (isLoading) {
    return <Loader fullScreen logo />;
  }

  const isSaveButtonLoading = isLoading || isLoadingPatchApiConfig;

  return (
    <>
      <DefaultScreenHeader
        backTo={generateNextPath(PATHS.DATA_ACCESS, {
          accountId,
        })}
        screenTitle={PAGE_TITLES.DATA_ACCESS_API_CONFIGURATION}
      />
      {(projects ?? []).length === 0 && (
        <EmptyState
          lockScroll={false}
          text={DATA_ACCESS_API_CONFIGURATION.empty.description}
          title={DATA_ACCESS_API_CONFIGURATION.empty.title}
          type={EmptyStatus.EMPTY}
        />
      )}
      <FormProvider defaultValues={currentApiConfigs} methods={methods}>
        <JupiterCardList>
          {(projects ?? []).map(project => {
            const customMetrics = metrics?.custom_metrics?.[project.id] ?? [];
            const standardMetrics = metrics?.standard_metrics ?? [];

            // Filter out custom metrics that have the same name as standard metrics
            const filteredCustomMetrics = filterDuplicateCustomMetrics(
              customMetrics,
              standardMetrics,
            );

            const metricOptions = [
              ...buildCompositeMetricOptions(filteredCustomMetrics, 'custom'),
              ...buildCompositeMetricOptions(standardMetrics, 'standard'),
            ];

            return (
              <ApiConfigCard
                key={project.id}
                controlPrefix={`configs.${project.id}`}
                metricOptions={metricOptions}
                projectId={project.id}
                title={project.name}
              />
            );
          })}
        </JupiterCardList>
      </FormProvider>
      <SaveChangesDrawer
        isSubmitting={isSaveButtonLoading}
        open={isDirty}
        buttonsOnly
        onDiscard={handleDiscard}
        onSubmit={handleSubmit(onSubmit)}
      />
    </>
  );
};

export default withPermissions(
  ApiConfiguration,
  undefined,
  'agent_studio_data_access',
);
